import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import BrandLogo from '@/components/BrandLogo';
import {
  Sheet,
  SheetContent,
  SheetTitle,
  SheetDescription,
} from '@/components/ui/sheet';

/**
 * PublicHeader — shared header for public (logged-out) pages such as the
 * landing page (/) and the pricing page (/pricing).
 *
 * - "Pricing" and "Log In" share one class string (PUBLIC_NAV_LINK_CLASS) so
 *   typography, padding, radius and hover background are identical on every
 *   viewport, including inside the mobile drawer.
 * - "Pricing" is hidden while the user is already on /pricing. "Log In" and
 *   "Get Started" are always rendered (never route-hidden).
 * - The logo keeps its natural aspect ratio (w-auto + object-contain) with a
 *   fixed height per breakpoint, so it never squishes horizontally.
 * - On very narrow phones (< 390px) there is not enough room for logo +
 *   Pricing + Log In + Get Started, so Pricing / Log In move into a small
 *   drawer while "Get Started" stays visible.
 */
export const PUBLIC_NAV_LINK_CLASS =
  'inline-flex items-center justify-center whitespace-nowrap h-9 px-2.5 sm:px-4 rounded-md ' +
  'text-sm font-medium leading-5 text-white bg-transparent hover:bg-slate-800 hover:text-white ' +
  'transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-emerald-400';

const DRAWER_BREAKPOINT_HIDE = 'hidden min-[390px]:inline-flex';
const DRAWER_BREAKPOINT_SHOW = 'min-[390px]:hidden';

export default function PublicHeader({
  isAuthenticated = false,
  dashboardPath = '/employer',
  testIdPrefix = 'header',
  logoTestId = 'header-home-link',
  sticky = false,
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const onPricingPage = pathname.replace(/\/+$/, '') === '/pricing';
  const showPricing = !onPricingPage;
  // Drawer is only needed when the Pricing link would crowd the header.
  const useDrawer = showPricing;
  const collapsible = useDrawer ? DRAWER_BREAKPOINT_HIDE : '';

  const go = (path) => {
    setDrawerOpen(false);
    navigate(path);
  };

  const secondaryLabel = isAuthenticated ? 'Dashboard' : 'Log In';
  const secondaryPath = isAuthenticated ? dashboardPath : '/login';
  const secondaryTestId = isAuthenticated
    ? `${testIdPrefix}-dashboard-btn`
    : `${testIdPrefix}-login-btn`;

  return (
    <header
      className={`bg-slate-900 text-white ${sticky ? 'sticky top-0 z-40' : ''}`}
      data-testid="public-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-2 sm:gap-4">
        <Link
          to="/"
          className="flex items-center min-w-0 shrink"
          data-testid={logoTestId}
          aria-label="ExpressSub home"
        >
          <BrandLogo variant="white" size="header" className="max-w-full object-left" />
        </Link>

        <nav
          className="flex items-center justify-end gap-1 sm:gap-4 shrink-0"
          data-testid="public-header-nav"
        >
          {showPricing && (
            <Link
              to="/pricing"
              className={`${PUBLIC_NAV_LINK_CLASS} ${collapsible}`}
              data-testid={`${testIdPrefix}-pricing-link`}
            >
              Pricing
            </Link>
          )}

          <button
            type="button"
            className={`${PUBLIC_NAV_LINK_CLASS} ${collapsible}`}
            onClick={() => go(secondaryPath)}
            data-testid={secondaryTestId}
          >
            {secondaryLabel}
          </button>

          {!isAuthenticated && (
            <Button
              className="h-9 px-3 sm:px-4 bg-emerald-600 hover:bg-emerald-700 text-white whitespace-nowrap"
              onClick={() => go('/register')}
              data-testid={`${testIdPrefix}-register-btn`}
            >
              Get Started
            </Button>
          )}

          {useDrawer && (
            <button
              type="button"
              className={`${PUBLIC_NAV_LINK_CLASS} !px-2 ${DRAWER_BREAKPOINT_SHOW}`}
              onClick={() => setDrawerOpen(true)}
              aria-label="Open menu"
              data-testid={`${testIdPrefix}-menu-btn`}
            >
              <Menu className="h-5 w-5" />
            </button>
          )}
        </nav>
      </div>

      {useDrawer && (
        <Sheet open={drawerOpen} onOpenChange={setDrawerOpen}>
          <SheetContent
            side="right"
            className="bg-slate-900 text-white border-slate-800 w-64 p-6 [&>button]:text-white"
            data-testid={`${testIdPrefix}-drawer`}
          >
            <SheetTitle className="sr-only">Menu</SheetTitle>
            <SheetDescription className="sr-only">Site navigation</SheetDescription>
            <div className="mt-8 flex flex-col gap-2">
              <Link
                to="/pricing"
                onClick={() => setDrawerOpen(false)}
                className={`${PUBLIC_NAV_LINK_CLASS} !justify-start w-full`}
                data-testid={`${testIdPrefix}-drawer-pricing-link`}
              >
                Pricing
              </Link>
              <button
                type="button"
                onClick={() => go(secondaryPath)}
                className={`${PUBLIC_NAV_LINK_CLASS} !justify-start w-full`}
                data-testid={`${testIdPrefix}-drawer-${isAuthenticated ? 'dashboard' : 'login'}-btn`}
              >
                {secondaryLabel}
              </button>
            </div>
          </SheetContent>
        </Sheet>
      )}
    </header>
  );
}
